// Block-level diff for the rendered review (SPEC §6 "Rendering the diff").
// Blocks are the top-level mdast nodes. They're matched by their exact source text with a
// sequence diff, so even a formatting-only change shows (as "modified, formatting only").
// Within a run of changes, removed and added blocks are paired by word similarity.
import { diffArrays, diffLines } from 'diff';
import type { Root, RootContent } from 'mdast';
import { parseMarkdown } from '../markdown/parse';
import { canonical } from '../markdown/units';

export type Block = {
  index: number;
  node: RootContent;
  /** 1-based, inclusive. */
  startLine: number;
  endLine: number;
  source: string;
};

export type DiffItem =
  | { kind: 'same'; head: Block; base: Block }
  | { kind: 'added'; head: Block }
  | { kind: 'modified'; head: Block; base: Block; formattingOnly: boolean }
  | { kind: 'removed'; base: Block };

export type FileDiff = {
  baseTree: Root;
  headTree: Root;
  items: DiffItem[];
  /** Line counts like GitHub's +/−. */
  added: number;
  removed: number;
};

export function blocksOf(md: string, tree: Root): Block[] {
  return tree.children.map((node, index) => ({
    index, node,
    startLine: node.position!.start.line,
    endLine: node.position!.end.line,
    source: md.slice(node.position!.start.offset!, node.position!.end.offset!),
  }));
}

const words = (s: string) => new Set(s.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []);
function similarity(a: Block, b: Block): number {
  const wa = words(a.source), wb = words(b.source);
  if (!wa.size && !wb.size) return 1;
  let common = 0;
  for (const w of wa) if (wb.has(w)) common++;
  return common / Math.max(wa.size, wb.size);
}

const PAIR_THRESHOLD = 0.3;

/** Monotone pairing of removed and added blocks of the same type, best similarity first. */
function pair(removed: Block[], added: Block[]): [number, number][] {
  const pairs: [number, number][] = [];
  let from = 0;
  removed.forEach((r, i) => {
    let best = -1, bestScore = PAIR_THRESHOLD;
    for (let j = from; j < added.length; j++) {
      if (added[j]!.node.type !== r.node.type) continue;
      const s = similarity(r, added[j]!);
      if (s >= bestScore) { best = j; bestScore = s; }
    }
    if (best >= 0) { pairs.push([i, best]); from = best + 1; }
  });
  return pairs;
}

export function diffFile(baseMd: string, headMd: string): FileDiff {
  const baseTree = parseMarkdown(baseMd);
  const headTree = parseMarkdown(headMd);
  const a = blocksOf(baseMd, baseTree);
  const b = blocksOf(headMd, headTree);
  const items: DiffItem[] = [];

  const parts = diffArrays(a.map((x) => x.source), b.map((x) => x.source));
  let ai = 0, bi = 0;
  for (let p = 0; p < parts.length; p++) {
    const part = parts[p]!;
    if (!part.added && !part.removed) {
      for (let k = 0; k < part.count; k++) items.push({ kind: 'same', base: a[ai + k]!, head: b[bi + k]! });
      ai += part.count; bi += part.count;
      continue;
    }
    let rem = 0, add = 0;
    if (part.removed) { rem = part.count; if (parts[p + 1]?.added) add = parts[++p]!.count; }
    else add = part.count;
    const R = a.slice(ai, ai + rem), A = b.slice(bi, bi + add);
    let ri = 0, aj = 0;
    for (const [i, j] of pair(R, A)) {
      while (ri < i) items.push({ kind: 'removed', base: R[ri++]! });
      while (aj < j) items.push({ kind: 'added', head: A[aj++]! });
      items.push({ kind: 'modified', base: R[i]!, head: A[j]!, formattingOnly: canonical(R[i]!.node) === canonical(A[j]!.node) });
      ri = i + 1; aj = j + 1;
    }
    while (ri < R.length) items.push({ kind: 'removed', base: R[ri++]! });
    while (aj < A.length) items.push({ kind: 'added', head: A[aj++]! });
    ai += rem; bi += add;
  }

  let added = 0, removed = 0;
  for (const part of diffLines(baseMd, headMd)) {
    if (part.added) added += part.count ?? 0;
    else if (part.removed) removed += part.count ?? 0;
  }
  return { baseTree, headTree, items, added, removed };
}

/** Indexes (into items) of changes, for previous/next navigation. */
export const changeIndexes = (items: DiffItem[]) => items.flatMap((it, i) => (it.kind === 'same' ? [] : [i]));
